import { ChangeEvent, useState } from "react";

import { useTranslator } from "@eo-locale/react";

import { string } from "yup";

import {
  FormControl,
  FormHelperText,
  FormErrorMessage,
  FormErrorIcon,
  HTMLWrapper,
  FormLabel,
  Input,
  Flex,
  VisuallyHidden
} from "@/storybook-components";
import type { Question } from "../question";

export const validate = async (
  answer: string,
  question: Question
): Promise<string[]> => {
  let schema = string();
  if (question.required) {
    schema = schema.required();
  }
  try {
    await schema.max(question.maxLength || 255).validate(answer);
    return [];
  } catch (err: any) {
    return err.errors;
  }
};

function SingleLineQuestion(props: Question): JSX.Element {
  const {
    id,
    question,
    required,
    maxLength,
    error,
    status,
    onChangeHandler,
    notes,
    hasMaxLength,
    existingValue
  } = props;
  const { translate } = useTranslator();
  const maxTextLength = maxLength || 255;

  const [inputText, setInputText] = useState(existingValue || "");

  const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
    setInputText(event.target.value);
    if (onChangeHandler) onChangeHandler(event.target.value);
  };

  return (
    <FormControl
      my={4}
      id={`${id}`}
      isInvalid={error?.length > 0 || false}
      isRequired={required || false}
      isDisabled={status === "saving"}
      className={error?.length > 0 ? "ehqQuestionError" : ""}
    >
      <FormLabel>
        <HTMLWrapper html={question} />
        {required && (
          <VisuallyHidden>{`* ${translate("common.required")}`}</VisuallyHidden>
        )}
      </FormLabel>
      <Input
        type="text"
        value={inputText}
        maxLength={maxTextLength}
        onChange={handleChange}
        size={{ base: "lg", lg: "md" }}
        fontSize="md !important"
        fontWeight="normal"
        data-testid="singleLineQuestion-input"
      />
      <Flex justifyContent="space-between">
        <FormHelperText color="gray.800">{notes || ""}</FormHelperText>
        {hasMaxLength && (
          <FormHelperText color="gray.800">{`${inputText.length}/${maxTextLength}`}</FormHelperText>
        )}
      </Flex>
      {error?.length > 0 && (
        <FormErrorMessage data-testid="singleLineQuestion-errorMessage">
          <FormErrorIcon />
          {translate(`errors.${error}`)}
        </FormErrorMessage>
      )}
    </FormControl>
  );
}

export { SingleLineQuestion };
